import React, { useState, useRef, useEffect } from "react";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { formatPrice } from "../../../../../utilities/formatPrice";
import localeText from "../../../../../locale";
import { useToastContext } from "../../../../../contexts/ToastContext/ToastContext";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import ReferralQrDialog from "../ReferralQrDialog/ReferralQrDialog";
import {
  REFERRAL_ITEMS,
  buildReferralUrl,
  filterReferralItems,
} from "../../../pages/EconomicCircle/constants/referralItems";
import { useThemeContext } from "../../../../../contexts/ThemeContext/ThemeContext";
import GetUserInfoService from "../../../../../service/GetUserInfoService";

const GAUGE_MAX = 350;
const getUserInfoService = new GetUserInfoService();

const EconomicCircle = ({
  directsInfo = [],
  allNodesInfo = [],
  currency = "€",
  username,
  userTypeInfo,
}) => {
  const [shareDropdownVisible, setShareDropdownVisible] = useState(false);
  const [qrDialogVisible, setQrDialogVisible] = useState(false);
  const [qrDialogUrl, setQrDialogUrl] = useState("");
  const [qrDialogTitleKey, setQrDialogTitleKey] = useState("");
  const dropdownRef = useRef(null);
  const { isSubnetLeader } = useThemeContext();

  const visibleItems = filterReferralItems(REFERRAL_ITEMS, { userTypeInfo, isSubnetLeader });

  const { showInfoToast } = useToastContext();

  useEffect(() => {
    if (!shareDropdownVisible) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShareDropdownVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [shareDropdownVisible]);

  // null = ainda carregando ou API sem o endpoint: mostra "—" em vez de um número errado
  const [networkCommissions, setNetworkCommissions] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("__extraconomy_token");
    if (!token) return undefined;
    getUserInfoService
      .getNetworkCommissions(token)
      .then((res) => {
        if (!cancelled && res?.messageCode === 200001) setNetworkCommissions(res.result);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const formatCommission = (value) =>
    networkCommissions ? `${currency} ${formatPrice(Number(value ?? 0) || 0)}` : "—";

  const allNodesCount = allNodesInfo.length || 0;

  const progress = Math.min(1, allNodesCount / GAUGE_MAX);
  const radius = 100;
  const strokeWidth = 22;
  const cx = 100;
  const cy = 90;
  const pathRadius = radius - strokeWidth / 2;
  const pathLength = Math.PI * pathRadius;
  const arcPath = `M ${cx - pathRadius} ${cy} A ${pathRadius} ${pathRadius} 0 0 1 ${cx + pathRadius} ${cy}`;

  const handleShareClick = (referralUrl) => {
    if (JSON.parse(localStorage.getItem("__extraconomy_ismobile__") || "false")) {
      window.ReactNativeWebView?.postMessage?.(
        JSON.stringify({ content: referralUrl, clipboard: true }),
      );
    } else {
      navigator.clipboard?.writeText(referralUrl);
    }
    showInfoToast({ summary: "copied", detail: "copiedToClipboard" });
  };

  const openQrDialog = (url, localeKey) => {
    setQrDialogUrl(url);
    setQrDialogTitleKey(localeKey);
    setQrDialogVisible(true);
  };

  return (
    <div
      ref={dropdownRef}
      className="card border-round-xl"
      style={{ position: "relative", border: "1px solid var(--theme-border, #EBEBEB)" }}
    >
      <div className="flex align-items-center justify-content-between mb-3">
        <div>
          <div className="m-0  text-md font-semibold" style={{ color: "var(--theme-text)" }}>
            {localeText("economicCircle")}
          </div>
          <div className="text-xs m-0 mt-1" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
            {localeText("yourNetworkOfInfluence")}
          </div>
        </div>
        <Button
          icon="pi pi-send"
          className="p-button-text p-button-plain p-no-outline border-none"
          label={<span className="underline">{localeText("share") || "Compartilhar"}</span>}
          style={{ color: "var(--theme-secondary)" }}
          iconPos="right"
          onClick={() => setShareDropdownVisible((v) => !v)}
        />
      </div>

      {shareDropdownVisible && (
        <div
          className="border-round-xl p-3"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            minHeight: "100%",
            zIndex: 10,
            backgroundColor: "var(--theme-background, #FFFFFF)",
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            border: "1px solid var(--theme-border, #EBEBEB)",
          }}
        >
          <div className="flex align-items-center justify-content-between mb-3">
            <div className="flex align-items-center gap-2">
              <i
                className="pi pi-sitemap"
                style={{ color: "var(--theme-secondary)", fontSize: "1.25rem" }}
              />
              <span className="font-semibold" style={{ color: "var(--theme-text)" }}>
                {localeText("shareReferrals") || "Compartilhe indicações"}
              </span>
            </div>
            <Button
              icon="pi pi-times"
              className="p-button-text p-button-rounded p-button-plain"
              onClick={() => setShareDropdownVisible(false)}
              aria-label={localeText("close")}
            />
          </div>
          <div className="flex flex-column gap-2">
            {username ? (
              visibleItems.map((item) => {
                const referralUrl = buildReferralUrl(
                  "https://extraconomy.com",
                  item,
                  username,
                );
                return (
                  <div
                    key={item.localeKey}
                    className="flex align-items-center gap-3 p-2 border-round-md"
                  >
                    <Button
                      className="p-button-text p-0 p-no-outline"
                      style={{
                        color: "var(--theme-text-muted, #5C5C5C)",
                      }}
                      label={<i className="pi pi-qrcode text-4xl"></i>}
                      onClick={() => openQrDialog(referralUrl, item.localeKey)}
                      aria-label={`QR Code ${localeText(item.localeKey)}`}
                    />
                    <div className="flex flex-column gap-2">
                      <div className="flex align-items-center gap-2">
                        <div
                          className="px-2 py-1 border-round-lg text-xs font-medium"
                          style={{
                            backgroundColor: item.backgroundColor,
                            color: item.color,
                          }}
                        >
                          {localeText(item.localeKey)}
                        </div>
                      </div>

                      <Button
                        label={localeText("share") || "Compartilhar"}
                        size="small"
                        icon="pi pi-copy"
                        iconPos="right"
                        className="p-button-text border-round-xl text-xs px-2 py-1 p-no-outline"
                        style={{
                          color: "var(--theme-text-muted, #5C5C5C)",
                          border: "1px solid var(--theme-border, #E0E0E0)",
                        }}
                        onClick={() => handleShareClick(referralUrl)}
                      />
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-color-secondary m-0 text-sm">
                {localeText("referralLinksExplanation")}
              </p>
            )}
          </div>
        </div>
      )}

      <ReferralQrDialog
        visible={qrDialogVisible}
        onHide={() => setQrDialogVisible(false)}
        url={qrDialogUrl}
        titleKey={qrDialogTitleKey}
      />

      <div
        className="flex justify-content-center align-items-end mb-3"
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "320px",
          minHeight: "200px",
          margin: "0 auto",
          aspectRatio: "200 / 90",
        }}
      >
        <svg
          viewBox="0 0 200 100"
          preserveAspectRatio="xMidYMax meet"
          style={{
            width: "100%",
            height: "100%",
            overflow: "visible",
            display: "block",
            verticalAlign: "bottom",
          }}
          aria-hidden
        >
          <defs>
            <linearGradient id="economicGaugeFill" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="var(--theme-secondary)" />
              <stop offset="100%" stopColor="#F5F5F5" />
            </linearGradient>
          </defs>
          <path
            d={arcPath}
            fill="none"
            stroke="var(--theme-border, #EBEBEB)"
            strokeWidth={strokeWidth}
            strokeLinecap="square"
          />
          <path
            d={arcPath}
            fill="none"
            stroke="url(#economicGaugeFill)"
            strokeWidth={strokeWidth}
            strokeLinecap="square"
            strokeDasharray={`${pathLength} ${pathLength}`}
            strokeDashoffset={pathLength * (1 - progress)}
            style={{ transition: "stroke-dashoffset 0.5s ease" }}
          />
        </svg>
        <div
          style={{
            position: "absolute",
            left: "50%",
            bottom: 20,
            transform: "translateX(-50%)",
            textAlign: "center",
          }}
        >
          <div className="text-3xl font-medium" style={{ color: "var(--theme-text)" }}>
            {allNodesCount}
          </div>
          <div className="text-sm" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
            {localeText("members")}
          </div>
        </div>
      </div>

      <div className="flex justify-content-center mb-3">
        <Link to={`/dashboard-economic-circle/members?tab=members`} className="w-full">
          <Button
            label={
              <span className="flex align-items-center justify-content-center text-xs font-medium">
                <i className="pi pi-sitemap mr-2"></i> {localeText("viewNetwork")}
              </span>
            }
            className="p-no-outline border-none border-round-lg w-full py-2"
            style={{
              backgroundColor: "var(--theme-background)",
              borderColor: "var(--theme-border, #e0e0e0)",
              color: "var(--theme-text)",
            }}
          />
        </Link>
      </div>

      <div className="border-top-1 pt-3" style={{ borderColor: "var(--theme-border, #EBEBEB)" }}>
        <Tooltip target=".network-commission-info" position="top" style={{ maxWidth: "18rem" }} />
        <div className="flex align-items-center">
          <div className="flex-1 flex flex-column align-items-center justify-content-center">
            <div className="text-xs text-600 uppercase mb-1 flex align-items-center gap-1">
              {localeText("networkReferralCommissionLabel")}
              <i
                className="pi pi-info-circle network-commission-info cursor-pointer"
                style={{ fontSize: "0.75rem" }}
                data-pr-tooltip={localeText("networkReferralCommissionInfo")}
                aria-label={localeText("networkReferralCommissionInfo")}
                tabIndex={0}
              />
            </div>
            <div className="font-bold text-sm" style={{ color: "var(--theme-text)" }}>
              {formatCommission(networkCommissions?.referral?.received)}
            </div>
          </div>
          <div
            style={{
              width: "1px",
              height: "2.5rem",
              background: "var(--theme-border, #e0e0e0)",
              margin: "0 1.5rem",
            }}
          />
          <div className="flex-1 flex flex-column align-items-center justify-content-center">
            <div className="text-xs text-600 uppercase mb-1 flex align-items-center gap-1">
              {localeText("networkSalesCommissionLabel")}
              <i
                className="pi pi-info-circle network-commission-info cursor-pointer"
                style={{ fontSize: "0.75rem" }}
                data-pr-tooltip={localeText("networkSalesCommissionInfo")}
                aria-label={localeText("networkSalesCommissionInfo")}
                tabIndex={0}
              />
            </div>
            <div className="font-bold text-sm" style={{ color: "var(--theme-text)" }}>
              {formatCommission(networkCommissions?.sales?.received)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(EconomicCircle);
